import React, { useState, useMemo, useEffect, useRef } from 'react';
import { STATUS_COLORS, ACTION_COLORS, STATUSES, FU_STATUSES, FU_STATUS_COLORS } from '../utils/lang';

/* ═══════════ STATUS BADGE ═══════════ */
export function StatusBadge({ value, colorMap }) {
  if (!value) return <span style={{ color: '#aaa', fontSize: 11 }}>—</span>;
  const m = colorMap || STATUS_COLORS;
  const c = m[value] || { bg: '#e2e3e5', tx: '#383d41' };
  return (
    <span style={{
      padding: '3px 9px', borderRadius: 4, fontSize: 10, fontWeight: 600,
      background: c.bg, color: c.tx, whiteSpace: 'nowrap', display: 'inline-block'
    }}>
      {value}
    </span>
  );
}

/* ═══════════ ACTION BADGE ═══════════ */
export function ActionBadge({ value }) {
  if (!value) return <span style={{ color: '#aaa', fontSize: 11 }}>—</span>;
  const c = ACTION_COLORS[value] || { bg: '#e2e3e5', tx: '#383d41' };
  return (
    <span style={{
      padding: '3px 9px', borderRadius: 4, fontSize: 10, fontWeight: 600,
      background: c.bg, color: c.tx, whiteSpace: 'nowrap', display: 'inline-block'
    }}>
      {value}
    </span>
  );
}

/* ═══════════ TYPE BADGE ═══════════ */
export function TypeBadge({ type }) {
  const isNew = type === 'New Client' || type === 'New';
  return (
    <span style={{
      padding: '2px 7px', borderRadius: 4, fontSize: 9, fontWeight: 700,
      background: isNew ? '#dbeafe' : '#f3e8ff', color: isNew ? '#1d4ed8' : '#7c3aed'
    }}>
      {isNew ? 'NEW' : 'OLD'}
    </span>
  );
}

/* ═══════════ ROLE BADGE ═══════════ */
export function RoleBadge({ role }) {
  const styles = {
    admin: { bg: '#fecaca', tx: '#991b1b' },
    manager: { bg: '#fef3c7', tx: '#92400e' },
    agent: { bg: '#d1fae5', tx: '#065f46' },
  };
  const s = styles[role] || styles.agent;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
      background: s.bg, color: s.tx, textTransform: 'uppercase'
    }}>
      {role}
    </span>
  );
}

/* ═══════════ STAT CARD ═══════════ */
export function StatCard({ label, value, color, th, note }) {
  var display = value;
  if (typeof value === 'number') display = value.toLocaleString();
  return (
    <div style={{
      flex: '1 1 130px', minWidth: 130,
      background: 'linear-gradient(155deg, ' + th.card + ' 0%, ' + th.stripe + ' 100%)',
      borderRadius: 14,
      padding: '15px 16px',
      border: '1px solid ' + th.border,
      boxShadow: '0 12px 28px rgba(15,23,42,0.08)',
      overflow: 'hidden',
      position: 'relative',
      transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease'
    }}
      onMouseEnter={function(e) {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 18px 34px rgba(15,23,42,0.14)';
        e.currentTarget.style.borderColor = th.accent + '66';
      }}
      onMouseLeave={function(e) {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 12px 28px rgba(15,23,42,0.08)';
        e.currentTarget.style.borderColor = th.border;
      }}
    >
      <div style={{
        position: 'absolute',
        width: 90,
        height: 90,
        right: -28,
        top: -28,
        borderRadius: '50%',
        background: color + '20'
      }} />
      <div style={{ fontSize: 11, color: th.sub, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color, fontFamily: 'Manrope,Cairo,sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {String(display)}
      </div>
      {note && <div style={{ fontSize: 9, fontWeight: 600, color: '#f59e0b', marginTop: 2 }}>{note}</div>}
    </div>
  );
}

/* ═══════════ CHART CARD ═══════════ */
export function ChartCard({ title, children, th }) {
  return (
    <div style={{
      background: 'linear-gradient(160deg, ' + th.card + ' 0%, ' + th.stripe + ' 100%)',
      borderRadius: 14,
      padding: 18,
      border: '1px solid ' + th.border,
      boxShadow: '0 12px 30px rgba(15,23,42,0.09)',
      transition: 'box-shadow .2s ease, border-color .2s ease'
    }}
      onMouseEnter={function(e) {
        e.currentTarget.style.boxShadow = '0 18px 38px rgba(15,23,42,0.15)';
        e.currentTarget.style.borderColor = th.accent + '55';
      }}
      onMouseLeave={function(e) {
        e.currentTarget.style.boxShadow = '0 12px 30px rgba(15,23,42,0.09)';
        e.currentTarget.style.borderColor = th.border;
      }}
    >
      <div style={{
        fontSize: 13, fontWeight: 700, color: th.text, marginBottom: 14,
        display: 'flex', alignItems: 'center', gap: 6
      }}>
        <div style={{ width: 3, height: 16, borderRadius: 2, background: th.accent }} />
        {title}
      </div>
      {children}
    </div>
  );
}

/* ═══════════ TOAST ═══════════ */
export function Toast({ msg, show }) {
  return (
    <div style={{
      position: 'fixed', bottom: 20, left: '50%',
      transform: 'translateX(-50%) translateY(' + (show ? '0' : '16') + 'px)',
      opacity: show ? 1 : 0, transition: 'all 0.3s', zIndex: 999,
      background: 'rgba(16,38,58,0.94)', color: '#d9fff9', padding: '11px 22px',
      borderRadius: 8, fontSize: 12, fontWeight: 600,
      boxShadow: '0 10px 28px rgba(2,6,23,0.3)', pointerEvents: 'none',
      backdropFilter: 'blur(6px)', border: '1px solid rgba(45,212,191,0.26)'
    }}>
      {msg}
    </div>
  );
}

/* ═══════════ DROPDOWN ═══════════ */
export function Dropdown({ value, options, onChange, placeholder, th }) {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearchText(''); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  var filtered = searchText
    ? options.filter(function(o) { return String(o).toLowerCase().indexOf(searchText.toLowerCase()) >= 0; })
    : options;

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        <button onClick={() => { setOpen(!open); setSearchText(''); }} style={{
          flex: 1, padding: '7px 10px', borderRadius: value ? '6px 0 0 6px' : 6,
          border: '1px solid ' + th.inpB, background: th.inp,
          textAlign: 'left', cursor: 'pointer', fontSize: 12,
          color: value ? th.inpT : th.sub,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderRight: value ? 'none' : ('1px solid ' + th.inpB),
          transition: 'background .15s ease, border-color .15s ease, box-shadow .15s ease'
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value || placeholder}
          </span>
          <span style={{ fontSize: 8, color: th.sub, flexShrink: 0, marginLeft: 4 }}>▼</span>
        </button>
        {value && (
          <button onClick={function(e) { e.stopPropagation(); onChange(value); }}
            style={{
              padding: '7px 7px', borderRadius: '0 6px 6px 0',
              border: '1px solid ' + th.inpB, borderLeft: 'none',
              background: th.inp, cursor: 'pointer', fontSize: 11,
              color: th.sub, display: 'flex', alignItems: 'center', lineHeight: 1
            }}
            title="Clear filter">✕</button>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 50,
          background: th.card, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          border: '1px solid ' + th.border, maxHeight: 240, overflow: 'hidden', display: 'flex', flexDirection: 'column'
        }}>
          <div style={{ padding: '6px 8px', borderBottom: '1px solid ' + th.border }}>
            <input ref={inputRef} value={searchText} onChange={function(e) { setSearchText(e.target.value); }}
              onKeyDown={function(e) {
                if (e.key === 'Escape') { setOpen(false); setSearchText(''); }
                if (e.key === 'Enter' && filtered.length > 0) {
                  onChange(filtered[0]);
                  setOpen(false);
                  setSearchText('');
                }
              }}
              placeholder="🔍 Type to search..."
              style={{
                width: '100%', padding: '6px 8px', borderRadius: 5, border: '1px solid ' + th.inpB,
                background: th.inp, fontSize: 11, outline: 'none', color: th.inpT, boxSizing: 'border-box'
              }} />
          </div>
          <div style={{ overflow: 'auto', maxHeight: 190 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '10px', fontSize: 11, color: th.sub, textAlign: 'center' }}>No match</div>
            )}
            {filtered.map(function(o) {
              return (
                <div key={o} onClick={function() { onChange(o); setOpen(false); setSearchText(''); }}
                  style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 12, color: th.text, background: o === value ? (th.accent + '18') : 'transparent' }}
                  onMouseEnter={function(e) { e.currentTarget.style.background = th.stripe; }}
                  onMouseLeave={function(e) { e.currentTarget.style.background = o === value ? (th.accent + '18') : 'transparent'; }}>
                  {(STATUSES.indexOf(o) >= 0 || FU_STATUSES.indexOf(o) >= 0) ? <StatusBadge value={o} colorMap={Object.assign({}, STATUS_COLORS, FU_STATUS_COLORS)} /> : o}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════ MULTI-SELECT DROPDOWN ═══════════ */
export function MultiDropdown({ value, options, onChange, placeholder, th }) {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(function() {
    if (!open) return;
    var handler = function(e) {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearchText(''); }
    };
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, [open]);

  useEffect(function() {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  var sel = value || [];
  var filtered = searchText
    ? options.filter(function(o) { return String(o).toLowerCase().indexOf(searchText.toLowerCase()) >= 0; })
    : options;

  var toggle = function(item) {
    var idx = sel.indexOf(item);
    if (idx >= 0) {
      var next = sel.filter(function(v) { return v !== item; });
      onChange(next);
    } else {
      onChange(sel.concat([item]));
    }
  };

  var selectAll = function() { onChange(options.slice()); setSearchText(''); };
  var clearAll = function() { onChange([]); setSearchText(''); };

  var label = sel.length === 0 ? placeholder
    : sel.length === 1 ? sel[0]
    : sel.length + ' selected';

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        <button onClick={function() { setOpen(!open); setSearchText(''); }} style={{
          flex: 1, padding: '7px 10px', borderRadius: sel.length > 0 ? '6px 0 0 6px' : 6,
          border: '1px solid ' + th.inpB, background: th.inp,
          textAlign: 'left', cursor: 'pointer', fontSize: 12,
          color: sel.length > 0 ? th.inpT : th.sub,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderRight: sel.length > 0 ? 'none' : ('1px solid ' + th.inpB),
          transition: 'background .15s ease, border-color .15s ease'
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}
          </span>
          <span style={{ fontSize: 8, color: th.sub, flexShrink: 0, marginLeft: 4 }}>▼</span>
        </button>
        {sel.length > 0 && (
          <button onClick={function(e) { e.stopPropagation(); onChange([]); }}
            style={{
              padding: '7px 7px', borderRadius: '0 6px 6px 0',
              border: '1px solid ' + th.inpB, borderLeft: 'none',
              background: th.inp, cursor: 'pointer', fontSize: 11,
              color: th.sub, display: 'flex', alignItems: 'center', lineHeight: 1
            }}
            title="Clear filter">✕</button>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 50,
          background: th.card, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          border: '1px solid ' + th.border, maxHeight: 280, overflow: 'hidden', display: 'flex', flexDirection: 'column',
          minWidth: 180
        }}>
          <div style={{ padding: '6px 8px', borderBottom: '1px solid ' + th.border }}>
            <input ref={inputRef} value={searchText} onChange={function(e) { setSearchText(e.target.value); }}
              onKeyDown={function(e) {
                if (e.key === 'Escape') { setOpen(false); setSearchText(''); }
              }}
              placeholder="🔍 Search..."
              style={{
                width: '100%', padding: '6px 8px', borderRadius: 5, border: '1px solid ' + th.inpB,
                background: th.inp, fontSize: 11, outline: 'none', color: th.inpT, boxSizing: 'border-box'
              }} />
          </div>
          <div style={{ display: 'flex', gap: 4, padding: '5px 8px', borderBottom: '1px solid ' + th.border }}>
            <button onClick={selectAll} style={{ flex: 1, padding: '4px 0', borderRadius: 4, border: '1px solid ' + th.border, background: th.accent + '18', color: th.accent, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Select All</button>
            <button onClick={clearAll} style={{ flex: 1, padding: '4px 0', borderRadius: 4, border: '1px solid ' + th.border, background: '#e74c3c18', color: '#e74c3c', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Clear</button>
          </div>
          <div style={{ overflow: 'auto', maxHeight: 200 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '10px', fontSize: 11, color: th.sub, textAlign: 'center' }}>No match</div>
            )}
            {filtered.map(function(o) {
              var checked = sel.indexOf(o) >= 0;
              return (
                <div key={o} onClick={function() { toggle(o); }}
                  style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12, color: th.text, background: checked ? (th.accent + '12') : 'transparent', display: 'flex', alignItems: 'center', gap: 8 }}
                  onMouseEnter={function(e) { e.currentTarget.style.background = th.stripe; }}
                  onMouseLeave={function(e) { e.currentTarget.style.background = checked ? (th.accent + '12') : 'transparent'; }}>
                  <span style={{
                    width: 16, height: 16, borderRadius: 3, border: '2px solid ' + (checked ? th.accent : th.border),
                    background: checked ? th.accent : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, fontSize: 10, color: '#fff', lineHeight: 1
                  }}>{checked ? '✓' : ''}</span>
                  {(STATUSES.indexOf(o) >= 0 || FU_STATUSES.indexOf(o) >= 0) ? <StatusBadge value={o} colorMap={Object.assign({}, STATUS_COLORS, FU_STATUS_COLORS)} /> : <span>{o}</span>}
                </div>
              );
            })}
          </div>
          {sel.length > 0 && (
            <div style={{ padding: '5px 8px', borderTop: '1px solid ' + th.border, fontSize: 10, color: th.sub, textAlign: 'center' }}>
              {sel.length + ' of ' + options.length + ' selected'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════ COPY CELL (hover to show copy icon) ═══════════ */
export function CopyCell({ value, color }) {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  const handleCopy = function(e) {
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(function() { setCopied(false); }, 1500);
  };
  return (
    <div onMouseEnter={function() { setHovered(true); }} onMouseLeave={function() { setHovered(false); setCopied(false); }}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, position: 'relative', cursor: 'default' }}>
      <span style={{ fontWeight: 700, color: color || 'inherit', fontSize: 11 }}>{value}</span>
      {hovered && !copied && (
        <span onClick={handleCopy} title="Copy"
          style={{ cursor: 'pointer', fontSize: 12, opacity: 0.6, display: 'inline-flex', alignItems: 'center', transition: 'opacity 0.15s' }}
          onMouseEnter={function(e) { e.currentTarget.style.opacity = 1; }}
          onMouseLeave={function(e) { e.currentTarget.style.opacity = 0.6; }}>
          📋
        </span>
      )}
      {copied && (
        <span style={{ fontSize: 9, color: '#16835e', fontWeight: 700, background: '#d1fae5', padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>Copied!</span>
      )}
    </div>
  );
}

/* ═══════════ COPY GUARD ═══════════ */
export function CopyGuard({ children, role }) {
  useEffect(function() {
    if (role === 'admin') return undefined;
    var block = function(e) { e.preventDefault(); };
    var bk = function(e) { if ((e.ctrlKey || e.metaKey) && 'cpsa'.indexOf(e.key) >= 0) e.preventDefault(); };
    document.addEventListener('copy', block);
    document.addEventListener('cut', block);
    document.addEventListener('contextmenu', block);
    document.addEventListener('keydown', bk);
    return function() {
      document.removeEventListener('copy', block);
      document.removeEventListener('cut', block);
      document.removeEventListener('contextmenu', block);
      document.removeEventListener('keydown', bk);
    };
  }, [role]);
  return (
    <div style={role !== 'admin' ? { userSelect: 'none', WebkitUserSelect: 'none' } : {}}>
      {children}
    </div>
  );
}

/* ═══════════ SORTABLE TABLE WITH PAGINATION ═══════════ */
export function SortableTable({ cols, data, th, empty, rtl, pageSize, rowClassName }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(0);
  const perPage = pageSize || 50;

  // Reset page when data length changes (not on every re-render)
  var dataLen = data ? data.length : 0;
  useEffect(function() { setPage(0); }, [dataLen]);

  const sorted = useMemo(function() {
    if (!sortKey) return data;
    return data.slice().sort(function(a, b) {
      var va = a[sortKey], vb = b[sortKey];
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
  }, [data, sortKey, sortDir]);

  var totalPages = Math.ceil(sorted.length / perPage);
  var pageData = sorted.slice(page * perPage, (page + 1) * perPage);

  var doSort = function(k) {
    if (sortKey === k) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(k);
      setSortDir('asc');
    }
    setPage(0);
  };

  return (
    <div style={{ background: th.card, borderRadius: 12, border: '1px solid ' + th.border, overflow: 'hidden', boxShadow: '0 12px 28px rgba(15,23,42,0.08)' }}>
      <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: th.tHead, position: 'sticky', top: 0, zIndex: 5 }}>
              {cols.map(function(c) {
                var arrow = sortKey === c.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
                return (
                  <th key={c.key}
                    onClick={function() { if (c.sortable !== false) doSort(c.key); }}
                    style={{
                      padding: '10px 10px', textAlign: rtl ? 'right' : 'left', fontWeight: 600,
                      color: th.sub, borderBottom: '1px solid ' + th.border, whiteSpace: 'nowrap',
                      fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.75,
                      cursor: c.sortable !== false ? 'pointer' : 'default',
                      background: th.tHead
                    }}>
                    {c.headerRender ? c.headerRender() : (c.label + arrow)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={cols.length} style={{ padding: 28, textAlign: 'center', color: th.sub }}>
                  {empty}
                </td>
              </tr>
            ) : pageData.map(function(r, i) {
              var rClass = rowClassName ? rowClassName(r) : '';
              return (
                <tr key={r._id || (page * perPage + i)} className={rClass} style={{ background: i % 2 === 1 ? th.stripe : 'transparent', transition: 'background .12s ease' }}
                  onMouseEnter={function(e) { e.currentTarget.style.background = th.accent + '14'; }}
                  onMouseLeave={function(e) { e.currentTarget.style.background = i % 2 === 1 ? th.stripe : 'transparent'; }}>
                  {cols.map(function(c) {
                    var cellVal = c.render ? c.render(r) : (r[c.key] != null ? String(r[c.key]) : '');
                    return (
                      <td key={c.key} style={Object.assign(
                        { padding: '9px 10px', borderBottom: '1px solid ' + th.border + '22', color: th.text },
                        c.style || {}
                      )}>
                        {cellVal}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* PAGINATION */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', borderTop: '1px solid ' + th.border, flexWrap: 'wrap', gap: 8
        }}>
          <span style={{ fontSize: 11, color: th.sub }}>
            {(page * perPage + 1) + ' - ' + Math.min((page + 1) * perPage, sorted.length) + ' of ' + sorted.length.toLocaleString()}
          </span>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <button onClick={function() { setPage(0); }} disabled={page === 0}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid ' + th.border, background: th.card, cursor: page === 0 ? 'default' : 'pointer', color: page === 0 ? th.border : th.text, fontSize: 11 }}>
              {'<<'}
            </button>
            <button onClick={function() { setPage(Math.max(0, page - 1)); }} disabled={page === 0}
              style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid ' + th.border, background: th.card, cursor: page === 0 ? 'default' : 'pointer', color: page === 0 ? th.border : th.text, fontSize: 11 }}>
              {'<'}
            </button>
            <span style={{ fontSize: 11, color: th.text, padding: '0 8px', fontWeight: 600 }}>
              {(page + 1) + ' / ' + totalPages}
            </span>
            <button onClick={function() { setPage(Math.min(totalPages - 1, page + 1)); }} disabled={page >= totalPages - 1}
              style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid ' + th.border, background: th.card, cursor: page >= totalPages - 1 ? 'default' : 'pointer', color: page >= totalPages - 1 ? th.border : th.text, fontSize: 11 }}>
              {'>'}
            </button>
            <button onClick={function() { setPage(totalPages - 1); }} disabled={page >= totalPages - 1}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid ' + th.border, background: th.card, cursor: page >= totalPages - 1 ? 'default' : 'pointer', color: page >= totalPages - 1 ? th.border : th.text, fontSize: 11 }}>
              {'>>'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════ FILTERS BAR ═══════════ */
export function FiltersBar({ search, setSearch, fAgent, setFAgent, fStatus, setFStatus, fAction, setFAction, dateFrom, setDateFrom, dateTo, setDateTo, agents, showAgent, showStatus, showAction, actions, extraFilters, ll, th, rtl, onClear }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
      <div style={{ flex: '1 1 160px', minWidth: 140, position: 'relative' }}>
        <input value={search} onChange={function(e) { setSearch(e.target.value); }}
          placeholder={ll.search}
          style={{
            width: '100%', padding: '8px 10px 8px 28px', borderRadius: 6,
            border: '1px solid ' + th.inpB, background: th.inp, fontSize: 12,
            outline: 'none', color: th.inpT, boxSizing: 'border-box',
            transition: 'border-color .15s ease, box-shadow .15s ease'
          }} />
        <span style={{
          position: 'absolute', left: rtl ? 'auto' : 9, right: rtl ? 9 : 'auto',
          top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: th.sub, pointerEvents: 'none'
        }}>&#8981;</span>
      </div>
      {showAgent && agents && agents.length > 0 && (
        <div style={{ minWidth: 140 }}>
          <MultiDropdown value={fAgent} options={agents}
            onChange={function(v) { setFAgent(v); }}
            placeholder={ll.allAgents} th={th} />
        </div>
      )}
      {showStatus && (
        <div style={{ minWidth: 130 }}>
          <MultiDropdown value={fStatus} options={['— Empty —'].concat(STATUSES)}
            onChange={function(v) { setFStatus(v); }}
            placeholder={ll.allStatuses} th={th} />
        </div>
      )}
      {showAction && actions && actions.length > 0 && (
        <div style={{ minWidth: 130 }}>
          <MultiDropdown value={fAction} options={['— Empty —'].concat(actions)}
            onChange={function(v) { setFAction(v); }}
            placeholder={ll.allActions || 'All Actions'} th={th} />
        </div>
      )}
      {extraFilters}
      <input type="date" value={dateFrom} onChange={function(e) { setDateFrom(e.target.value); }}
        style={{ padding: '7px 8px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 11, color: th.inpT, outline: 'none', transition: 'border-color .15s ease, box-shadow .15s ease' }} />
      <span style={{ color: th.sub, fontSize: 11 }}>to</span>
      <input type="date" value={dateTo} onChange={function(e) { setDateTo(e.target.value); }}
        style={{ padding: '7px 8px', borderRadius: 6, border: '1px solid ' + th.inpB, background: th.inp, fontSize: 11, color: th.inpT, outline: 'none', transition: 'border-color .15s ease, box-shadow .15s ease' }} />
      {(search || fAgent.length || fStatus.length || fAction.length || dateFrom || dateTo) && (
        <button onClick={onClear}
          style={{ background: 'none', border: '1px solid ' + th.border, borderRadius: 6, padding: '7px 12px', fontSize: 11, cursor: 'pointer', color: th.sub, transition: 'all .15s ease' }}>
          {'x ' + ll.clear}
        </button>
      )}
    </div>
  );
}

/* ═══════════ UPLOAD BUTTON ═══════════ */
export function UploadButton({ label, bg, color, onFile, accept }) {
  const inputRef = useRef(null);
  return (
    <div style={{ display: 'inline-block' }}>
      <button onClick={function() { inputRef.current.click(); }}
        style={{ background: bg, color, border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'transform .12s ease, filter .12s ease, box-shadow .12s ease', boxShadow: '0 8px 18px rgba(15,23,42,0.15)' }}
        onMouseEnter={function(e) { e.currentTarget.style.filter = 'brightness(1.06)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={function(e) { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}>
        {'↑ ' + label}
      </button>
      <input ref={inputRef} type="file" accept={accept || '.xlsx,.xls,.csv'} style={{ display: 'none' }}
        onChange={function(e) {
          if (e.target.files[0]) {
            onFile(e.target.files[0]);
            e.target.value = '';
          }
        }} />
    </div>
  );
}

/* ═══════════ LOADING SPINNER ═══════════ */
export function Spinner({ th, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, gap: 10 }}>
      <div style={{
        width: 24, height: 24, border: '3px solid ' + th.border,
        borderTop: '3px solid ' + th.accent, borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      {text && <span style={{ color: th.sub, fontSize: 12 }}>{text}</span>}
    </div>
  );
}

/* ═══════════ MODAL ═══════════ */
export function Modal({ show, onClose, title, children, th }) {
  useEffect(function() {
    if (!show) return undefined;
    var onKey = function(e) { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, [show, onClose]);

  if (!show) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      backdropFilter: 'blur(2px)', animation: 'fadeIn .15s ease'
    }} onClick={onClose}>
      <div onClick={function(e) { e.stopPropagation(); }}
        style={{
          background: th.card, borderRadius: 14, padding: 24, width: '100%', maxWidth: 440,
          border: '1px solid ' + th.border, boxShadow: '0 24px 50px rgba(2,6,23,0.32)',
          maxHeight: '85vh', overflow: 'auto', animation: 'slideUp .2s ease'
        }}>
        <style>{'@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes slideUp{from{opacity:0;transform:translateY(10px) scale(.99)}to{opacity:1;transform:translateY(0) scale(1)}}'}</style>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: th.text }}>{title}</h2>
          <button onClick={onClose}
            style={{ background: th.stripe, border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', color: th.sub, fontSize: 14 }}>
            x
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
